import { DropDownButton, DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import React, { useMemo } from 'react';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronDownIcon } from '@progress/kendo-svg-icons';
import { Label } from '@progress/kendo-react-labels';
import {
  useSearchContext,
  useViewsContext,
  useGlobalizationContext,
  useGetView,
  useDocumentsContext,
  useDocumentFormContext,
  useNotificationContext,
  useCurrentUserContext,
  useGetCtsWithCreatePermission,
  useLockDocuments,
  useUnlockDocuments,
  useDeleteDocuments,
  useGetUserPermissions,
  useTranslation
} from '@cs-cms/state-management';
import { useCpsDialog } from '../../../common/useCpsDialog';
import { FilterActionsContextMenu } from './FilterActionsContextMenu';
import {
  ACTION_GROUPS,
  ActionItemId,
  ConfirmDialogType,
  DropDownButtonItem,
  TooManyRequestsTimer
} from './FilterActions.types';
import { buildFilterActionItems } from './buildFilterActionItems';
import { FilterActionHandlerContext, handleFilterActionClick } from './filterActionHandlers';
import { FilterActionsExportDialog } from './FilterActionsExportDialog';
import { useFilterActionsTooManyRequestsTimer } from './useFilterActionsTooManyRequestsTimer';

interface FilterActionsProps {
  onMountActions?: (actions: FilterActionsHandle) => void;
}

export interface FilterActionsHandle {
  openContextMenu: (x: number, y: number) => void;
}

export const FilterActions: React.FC<FilterActionsProps> = ({ onMountActions }) => {
  const { data, queryRequest, handleTriggerExportToCsv } = useSearchContext();
  const { selectedView } = useViewsContext();
  const { selectedLocale } = useGlobalizationContext();
  const { data: currentView } = useGetView(selectedView, selectedLocale);
  const { selectedDocuments, downloadFiles } = useDocumentsContext();
  const [dialogVisible, setDialogVisible] = React.useState<ConfirmDialogType | undefined>(undefined);
  const { openNewDocumentForm, openEditDocumentForm } = useDocumentFormContext();
  const [tooManyRequestsTimer, setTooManyRequestsTimer] = React.useState<TooManyRequestsTimer | undefined>(undefined);
  const { showToast } = useNotificationContext();
  const { showDialog, DialogComponent } = useCpsDialog();
  const { selectedIndex } = useCurrentUserContext();
  const { data: ctsWithCreatePermissions } = useGetCtsWithCreatePermission(selectedIndex?.id);
  const { mutate: mutateLockDocuments } = useLockDocuments();
  const { mutate: mutateUnlockDocuments } = useUnlockDocuments();
  const { mutate: mutateDeleteDocuments } = useDeleteDocuments();
  const { data: permissions } = useGetUserPermissions();
  const { translate } = useTranslation(selectedLocale);
  const [contextMenuPosition, setContextMenuPosition] = React.useState<{ x: number; y: number } | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  useFilterActionsTooManyRequestsTimer({
    tooManyRequestsTimer,
    setTooManyRequestsTimer,
    dialogVisible,
    setDialogVisible
  });

  const actionsRef = React.useRef({
    openContextMenu: (x: number, y: number) => {
      setContextMenuPosition({ x, y });
    }
  });

  const handleContainerRef = (node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node && onMountActions) {
      onMountActions(actionsRef.current);
    }
  };

  const selectedDocArray = useMemo(() => selectedDocuments ?? [], [selectedDocuments]);
  const isAddHidden = ctsWithCreatePermissions === undefined || ctsWithCreatePermissions.length === 0;

  const items = useMemo(
    () =>
      buildFilterActionItems({
        translate,
        isAddHidden,
        canExportCsv: Boolean(permissions?.csvExport),
        selectedDocuments: selectedDocArray
      }),
    [translate, isAddHidden, permissions?.csvExport, selectedDocArray]
  );

  const handlerContext = useMemo((): FilterActionHandlerContext => {
    return {
      translate,
      showDialog,
      showToast,
      selectedDocuments,
      selectedIndex,
      downloadFiles,
      mutateLockDocuments,
      mutateUnlockDocuments,
      mutateDeleteDocuments,
      openNewDocumentForm,
      openEditDocumentForm,
      exportContext: {
        dataTotalCount: data?.totalCount,
        queryRequest,
        currentViewFieldNames: currentView?.fields.map(column => column.name),
        selectedLocale,
        selectedView,
        handleTriggerExportToCsv,
        tooManyRequestsTimer,
        setTooManyRequestsTimer,
        setDialogVisible
      }
    };
  }, [
    translate,
    showDialog,
    showToast,
    selectedDocuments,
    selectedIndex,
    downloadFiles,
    mutateLockDocuments,
    mutateUnlockDocuments,
    mutateDeleteDocuments,
    openNewDocumentForm,
    openEditDocumentForm,
    data?.totalCount,
    queryRequest,
    currentView?.fields,
    selectedLocale,
    selectedView,
    handleTriggerExportToCsv,
    tooManyRequestsTimer
  ]);

  const handleActionClick = (item: DropDownButtonItem): void => {
    if (item.isDisabled || item.id === ActionItemId.GROUP) {
      return;
    }
    handleFilterActionClick(item.id, handlerContext, selectedDocArray);
  };

  const itemClicked = (event: DropDownButtonItemClickEvent): void => {
    handleActionClick(event.item);
  };

  const handleItemRender = (li: { item: DropDownButtonItem; itemIndex: number }) => {
    return (
      <div className={li.item.className || `filterActionButtonOption`}>
        {li.item.svgIcon && <SvgIcon icon={li.item.svgIcon} />}
        <Label key={li.item.id}>{li.item.text}</Label>
      </div>
    );
  };

  const handleContextMenuItemClick = (item: DropDownButtonItem) => {
    setContextMenuPosition(null);
    handleActionClick(item);
  };

  return (
    <div>
      <div ref={handleContainerRef}>
        <DropDownButton
          endIcon={<SvgIcon icon={chevronDownIcon} />}
          text={translate('custom.toolbar.actions.title')}
          items={items}
          itemRender={handleItemRender}
          size={'medium'}
          themeColor={'base'}
          fillMode="solid"
          onItemClick={itemClicked}
          popupSettings={{
            popupClass: 'filterActionButtonPopup'
          }}
        />
      </div>

      <FilterActionsContextMenu
        position={contextMenuPosition}
        items={items.filter(x => x.groupId !== ACTION_GROUPS.GRID_GROUP)}
        onItemClick={handleContextMenuItemClick}
        onClose={() => setContextMenuPosition(null)}
      />

      {dialogVisible && (
        <FilterActionsExportDialog
          dialogVisible={dialogVisible}
          tooManyRequestsTimer={tooManyRequestsTimer}
          translate={translate}
          onConfirm={() => setDialogVisible(undefined)}
        />
      )}
      {DialogComponent}
    </div>
  );
};

